export const ASSESSMENT_COLORS = {
  POSITIVE: '9ad39d',
  CAUTION: 'f1c40f',
  UNKNOWN: '9ca3af',
  NEGATIVE: 'e74c3c',
}

export const ASSESSMENT_STATUS_META = {
  ASSESSED: {
    label: 'Avaliado',
    description: 'A fonte informou um valor para esta dimensão.',
  },
  NOT_ASSESSED: {
    label: 'Não avaliado',
    description: 'Esta dimensão ainda não foi avaliada.',
  },
  NOT_REPORTED: {
    label: 'Não informado',
    description: 'A fonte consultada não informou um valor para esta dimensão.',
  },
  NOT_APPLICABLE: {
    label: 'Não aplicável',
    description: 'Esta dimensão não se aplica ao contexto avaliado.',
  },
}

const LEGACY_DIMENSION_META = {
  metabolite: {
    code: 'GREENBOOK_METABOLITE_RELEVANCE',
    name: 'Concentração ou relevância química',
  },
  therapeuticEffect: {
    code: 'GREENBOOK_THERAPEUTIC_RELEVANCE',
    name: 'Relevância terapêutica',
  },
}

const LEGACY_VALUE_LABELS = {
  high: 'Alta',
  medium: 'Moderada',
  low: 'Baixa',
}

/**
 * Normaliza uma cor hexadecimal recebida da API para uso no CSS.
 *
 * @param {string|null|undefined} color - Cor com ou sem cerquilha
 * @returns {string} Cor hexadecimal válida para apresentação
 */
export function normalizeAssessmentColor(color) {
  const fallback = ASSESSMENT_COLORS.UNKNOWN
  const normalized = String(color || fallback).replace(/^#/, '')
  return `#${normalized}`
}

/**
 * Converte a relevância legada em uma avaliação apresentável.
 *
 * @param {object|null} relevance - Relevância Greenbook legada
 * @param {'metabolite'|'therapeuticEffect'} itemType - Tipo da associação
 * @returns {object|null} Avaliação compatível com o novo painel
 */
export function buildLegacyAssessment(relevance, itemType) {
  if (!relevance?.level) return null

  const dimension = LEGACY_DIMENSION_META[itemType]
  return {
    id: `legacy:${dimension.code}`,
    status: 'ASSESSED',
    context: null,
    comment: null,
    dimension: {
      ...dimension,
      description: '',
      hexColor: '156d2d',
      framework: {
        code: 'GREENBOOK',
        name: 'Greenbook',
        description: '',
        hexColor: '156d2d',
      },
    },
    value: {
      code: relevance.level.toUpperCase(),
      label: LEGACY_VALUE_LABELS[relevance.level] || relevance.level,
      description: relevance.description || '',
      hexColor: relevance.hexColor || ASSESSMENT_COLORS.UNKNOWN,
      rank: null,
    },
    sourceDocument: null,
  }
}

/**
 * Garante uma lista de avaliações para tooltip e modal.
 *
 * @param {Array} assessments - Avaliações retornadas pela API
 * @param {object|null} relevance - Relevância legada para compatibilidade
 * @param {'metabolite'|'therapeuticEffect'} itemType - Tipo da associação
 * @returns {Array} Avaliações normalizadas
 */
export function normalizeAssessments(assessments = [], relevance, itemType) {
  if (assessments.length > 0) return assessments

  const legacyAssessment = buildLegacyAssessment(relevance, itemType)
  return legacyAssessment ? [legacyAssessment] : []
}

/**
 * Resolve a cor exibida para uma avaliação, respeitando os estados globais.
 *
 * @param {object|null} assessment - Avaliação clínica ou química
 * @returns {string} Cor em formato hexadecimal
 */
export function getAssessmentColor(assessment) {
  if (!assessment || assessment.status !== 'ASSESSED') {
    return normalizeAssessmentColor(ASSESSMENT_COLORS.UNKNOWN)
  }

  return normalizeAssessmentColor(
    assessment.value?.hexColor
      || assessment.dimension?.hexColor
      || assessment.dimension?.framework?.hexColor,
  )
}

/**
 * Retorna o texto humanamente legível de uma avaliação.
 *
 * @param {object|null} assessment - Avaliação clínica ou química
 * @returns {string} Label do valor ou do estado
 */
export function getAssessmentLabel(assessment) {
  if (assessment?.status === 'ASSESSED') {
    return assessment.value?.label || 'Avaliado'
  }

  return ASSESSMENT_STATUS_META[assessment?.status]?.label || 'Não avaliado'
}

/**
 * Explica se o valor foi importado da fonte ou calculado pelo Greenbook.
 *
 * @param {object|null} assessment - Avaliação clínica ou química
 * @returns {string|null} Texto curto de proveniência para a interface
 */
export function getAssessmentProvenanceLabel(assessment) {
  const context = assessment?.context
  if (context?.assessmentOrigin !== 'REPORTED_BY_SOURCE') return null

  const reporter = context.reportedBy || 'fonte externa'
  if (context.analysisState === 'PENDING_CLAIM_ANALYSIS') {
    return `Reportado por ${reporter} · análise clínica pendente`
  }
  return `Reportado por ${reporter}`
}

/**
 * Escolhe a cor principal do chip a partir da avaliação mais bem ranqueada.
 *
 * @param {Array} assessments - Avaliações retornadas pela API
 * @param {object|null} relevance - Relevância legada
 * @param {'metabolite'|'therapeuticEffect'} itemType - Tipo da associação
 * @returns {string} Cor da borda do chip
 */
export function getPrimaryAssessmentColor(assessments, relevance, itemType) {
  const normalized = normalizeAssessments(assessments, relevance, itemType)
  const preferredDimensionCode = LEGACY_DIMENSION_META[itemType]?.code
  const preferredAssessments = normalized.filter(
    (assessment) => assessment.dimension?.code === preferredDimensionCode,
  )
  const colorCandidates = preferredAssessments.length > 0
    ? preferredAssessments
    : normalized
  const ordered = [...colorCandidates].sort(
    (left, right) => (right.value?.rank || 0) - (left.value?.rank || 0),
  )

  return getAssessmentColor(ordered[0])
}
